import { useState } from 'react';
import { Link } from 'react-router';
import { Sparkles } from 'lucide-react';
import type { RequestView, SimilarRecords, TriageSuggestion } from '@vwt/contracts';
import { Badge, Button, Card } from '@vwt/ui';
import { useApiMutation } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { KeyLabel, PriorityLabel, RequestStatusBadge } from '../../components/badges.js';
import { CreateWorkDialog } from '../work/create-work.js';

/**
 * AI duplicate judgment for triage (ADR-0025): the model reads the candidates found by text and
 * keeps those that describe the same problem. Marking a duplicate still goes through the normal
 * triage action.
 */
export function AiDuplicateCheck({ request, onMark }: { request: RequestView; onMark: (candidate: { id: string; key: string; title: string }) => void }) {
  const { t } = useTranslation();
  const check = useApiMutation('findSimilar');
  const [result, setResult] = useState<SimilarRecords | null>(null);
  const run = async () => {
    try {
      const response = await check.mutateAsync({ body: { kind: 'REQUEST', title: request.title || '—', descriptionMarkdown: [request.problemMarkdown, request.expectedOutcome ?? ''].join('\n\n'), excludeId: request.id, ai: true } });
      setResult(response.data);
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <Button size="sm" variant="secondary" className="self-start" loading={check.isPending} onClick={() => void run()} data-testid="ai-duplicates">
        <Sparkles /> {t('similar.checkRequests')}
      </Button>
      {result ? (
        result.candidates.length ? (
          <ul className="divide-y divide-border">
            {result.candidates.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                <KeyLabel value={c.key} />
                <Link to={`/requests/${c.key}`} className="min-w-0 flex-1 truncate hover:underline">
                  {c.title}
                </Link>
                <RequestStatusBadge status={c.status as RequestView['status']} />
                <Badge tone={c.confidence === 'HIGH' ? 'warning' : 'outline'}>{t(`similar.confidence.${c.confidence}`)}</Badge>
                <Button size="sm" variant="ghost" onClick={() => onMark({ id: c.id, key: c.key, title: c.title })}>
                  {t('requests.duplicates.mark')}
                </Button>
                {c.reason ? <p className="w-full text-xs text-fg-muted">{c.reason}</p> : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-fg-subtle">{t('similar.aiNone')}</p>
        )
      ) : null}
    </div>
  );
}

/**
 * Triage suggestion (ADR-0025): priority, team, project, work type and labels proposed by the AI
 * for this request. Nothing changes until the triager applies a part.
 */
export function TriageAssist({ request, onPatch }: { request: RequestView; onPatch: (body: Record<string, unknown>) => Promise<boolean> }) {
  const { t } = useTranslation();
  const suggest = useApiMutation('suggestTriage');
  const [suggestion, setSuggestion] = useState<TriageSuggestion | null>(null);
  const [creating, setCreating] = useState(false);
  const run = async () => {
    try {
      setSuggestion((await suggest.mutateAsync({ params: { id: request.id } })).data);
    } catch (error) {
      toastError(error);
    }
  };
  return (
    <Card className="mt-4 flex flex-col gap-2 px-3 py-3" data-testid="triage-assist">
      <div className="flex items-center gap-2">
        <Sparkles className="size-4 text-accent" aria-hidden />
        <h3 className="text-sm font-semibold">{t('triageAssist.title')}</h3>
        {suggestion?.generatedBy === 'fixture' ? <Badge tone="outline">{t('similar.fixture')}</Badge> : null}
      </div>
      {!suggestion ? (
        <>
          <p className="text-xs text-fg-subtle">{t('triageAssist.hint')}</p>
          <Button size="sm" variant="secondary" className="self-start" loading={suggest.isPending} onClick={() => void run()}>
            {t('triageAssist.suggest')}
          </Button>
        </>
      ) : (
        <dl className="flex flex-col gap-2 text-sm">
          <Row label={t('requests.columns.priority')} action={suggestion.priority !== request.priority ? <ApplyButton onApply={() => onPatch({ priority: suggestion.priority })} /> : null}>
            <PriorityLabel priority={suggestion.priority} />
            {suggestion.priorityReason ? <p className="text-xs text-fg-muted">{suggestion.priorityReason}</p> : null}
          </Row>
          {suggestion.team ? (
            <Row label={t('requests.detail.triageTeam')} action={suggestion.team.id !== request.triageTeam?.id ? <ApplyButton onApply={() => onPatch({ triageTeamId: suggestion.team!.id })} /> : null}>
              {suggestion.team.name}
            </Row>
          ) : null}
          {suggestion.project ? (
            <Row label={t('work.columns.project')}>
              {suggestion.project.name}
              {suggestion.project.id !== request.project?.id ? <p className="text-xs text-fg-subtle">{t('triageAssist.projectHint')}</p> : null}
            </Row>
          ) : null}
          <Row label={t('work.columns.type')}>{t(`status.workType.${suggestion.workType}`)}</Row>
          {suggestion.labels.length ? <Row label={t('work.columns.labels')}>{suggestion.labels.join(', ')}</Row> : null}
          {suggestion.missingInformation ? (
            <Row label={t('triageAssist.ask')}>
              <p className="text-xs">{suggestion.missingInformation}</p>
            </Row>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              {t('triageAssist.createWork')}
            </Button>
            <Button size="sm" variant="ghost" loading={suggest.isPending} onClick={() => void run()}>
              {t('triageAssist.again')}
            </Button>
          </div>
        </dl>
      )}
      {creating && suggestion ? (
        <CreateWorkDialog
          open
          onOpenChange={(open) => !open && setCreating(false)}
          defaults={{
            requestId: request.id,
            title: request.title,
            type: suggestion.workType,
            priority: suggestion.priority,
            labels: suggestion.labels,
            ...(suggestion.summary ? { descriptionMarkdown: suggestion.summary } : {}),
            ...(suggestion.project ? { projectId: suggestion.project.id } : request.project ? { projectId: request.project.id } : {}),
            ...(suggestion.team ? { teamId: suggestion.team.id } : request.triageTeam ? { teamId: request.triageTeam.id } : {}),
          }}
        />
      ) : null}
    </Card>
  );
}

function Row({ label, action, children }: { label: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="flex items-start gap-2">
        <div className="min-w-0 flex-1">{children}</div>
        {action}
      </dd>
    </div>
  );
}

function ApplyButton({ onApply }: { onApply: () => Promise<boolean> }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        await onApply();
        setBusy(false);
      }}
    >
      {t('triageAssist.apply')}
    </Button>
  );
}
